'use client'

import {
  type IBasicUser,
  type IChatLiteMessage,
  type IChatResponse,
  IMessagesUnreadQuote,
  type IMeta,
  type IRefMessages,
  type ISendMessageResponse,
} from '@repo/sdk'
import { ArrowLocation, useSubscribeToChatChannel } from '@repo/ui'
import { useRef, useState } from 'react'

import { ChatInput, useChatInput } from './chat-input.tsx'
import { ChatNext } from './chat-next.tsx'

interface IProps {
  channel: string
  chatId: number
  data: IMessagesUnreadQuote
  eventName: string
  getMessages: (
    chatId: number,
    params: IMeta | undefined | { current_page: number; per_page?: number | undefined },
  ) => Promise<IChatResponse | void>
  markChatMessagesAsRead: (chatId: number) => Promise<void>
  sendMessage: (chatId: number, message: string) => Promise<ISendMessageResponse | void>

  userData?: Partial<IBasicUser>
}

export const ChatLite = ({
  channel,
  chatId,
  data,
  eventName,
  getMessages,
  markChatMessagesAsRead,
  sendMessage,
  userData,
}: IProps) => {
  const refMessages = useRef<IRefMessages>(undefined)
  const [isNewMessageEvent] = useState(true)

  useSubscribeToChatChannel({
    channel,
    chatId,
    event: eventName,
    isAdmin: false,
    loadId: data.load_id,
  })

  const parserMessages = (message: IChatLiteMessage) => {
    const isSystemMessage = message.user.name === 'System'

    if (isSystemMessage && message.message?.search('The status changed') !== -1) {
      const indexLastSpace = message.message.lastIndexOf(' ')
      return (
        <div className={'my-6 flex items-center justify-center text-sm text-gray-800'}>
          <p className="mr-2 text-gray-500">The status of</p>
          <div className="mr-2 flex justify-center">
            <ArrowLocation data={data} />
          </div>
          <p className={'mr-2 text-gray-500'}>is changed to</p>
          <p className={'font-extrabold'}>{message.message.slice(indexLastSpace)}</p>
        </div>
      )
    }

    if (isSystemMessage) {
      const indexSpace = message.message.indexOf(' ')
      return (
        <div className={'my-6 flex justify-center text-gray-500'}>
          <p className={'font-extrabold'}>{message.message.slice(0, indexSpace - 1)}</p>
          <p>{message.message.slice(indexSpace - 1)}</p>
        </div>
      )
    }
  }

  const chatInputProps = useChatInput({
    chatId,
    isAdmin: false,
    loadId: data.load_id,
    sendMessage: async message => await sendMessage(chatId, message),
    userId: userData?.id,
  })

  return (
    <div
      className={
        'flex h-[calc(100%-141px)] w-full flex-col gap-2 border-t-4 border-[#f7fafc] bg-white'
      }
    >
      <ChatNext
        chatId={chatId}
        className={'w-100'}
        fetch={params => getMessages(chatId, params)}
        isNewMessageEvent={isNewMessageEvent}
        markMessagesAsRead={async () => chatId && markChatMessagesAsRead(chatId)}
        parserMessages={parserMessages}
        ref={refMessages}
        userData={userData}
      />
      <ChatInput {...chatInputProps} />
    </div>
  )
}
